import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box, { type BoxProps } from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { RemoteServiceErrorResponse } from "core/entities/CentrixTypes";
import { useTranslation } from "react-i18next";
import { JsonDisplay } from "./JsonDisplay";

export function ApiErrorDisplayAccordion({
  errorResponse,
  variant = "outlined",
  boxProps,
  clearError,
}: {
  errorResponse?: RemoteServiceErrorResponse | null;
  variant?: "outlined" | "elevation";
  boxProps?: BoxProps;
  clearError?: () => void;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:ErrorDisplayAccordion",
  });
  if (!errorResponse || !errorResponse.error) {
    return null;
  }

  const { code, message, details, data, validationErrors } =
    errorResponse.error;

  return (
    <Box {...boxProps}>
      <Accordion variant={variant}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="error-content"
          id="error-header"
          sx={{
            backgroundColor: (theme) =>
              theme.vars.palette.Alert.errorStandardBg,
            color: (theme) => theme.vars.palette.Alert.errorColor,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <ErrorOutlineIcon
              fontSize="small"
              sx={{ color: (theme) => theme.vars.palette.Alert.errorIconColor }}
            />
            <Typography>{message ?? t("summaryText")}</Typography>
            {clearError && (
              <IconButton
                aria-label={t("clearError")}
                onClick={clearError}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: (theme) =>
              theme.vars.palette.Alert.errorStandardBg,
          }}
        >
          <Stack gap={1}>
            <Typography variant="body1">
              <strong>{t("message")}:</strong> {message}
            </Typography>

            <Typography variant="body1">
              <strong>{t("details")}:</strong> <pre>{details}</pre>
            </Typography>

            <Typography variant="body1">
              <strong>{t("validationErrors")}:</strong>
            </Typography>
            <List dense disablePadding>
              {validationErrors?.map((validationError, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: had no reasonable unique key
                <ListItem key={index} dense disableGutters disablePadding>
                  <ListItemText
                    primary={validationError.message}
                    secondary={
                      validationError.members &&
                      validationError.members.length > 0
                        ? t("membersList", {
                            members: validationError.members.join(", "),
                          })
                        : null
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Typography variant="body1">
              <strong>{t("data")}:</strong>
            </Typography>
            <JsonDisplay data={data} />
            <Typography variant="body1">
              <strong>{t("code")}:</strong> {code}
            </Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
